/**
 * Replace YouTube links inside an HTML string with responsive embedded
 * iframes. Handles the three common URL shapes:
 *   - https://www.youtube.com/watch?v=ID
 *   - https://youtu.be/ID
 *   - https://www.youtube.com/embed/ID
 *
 * Replacement happens at the <p>-wrapping level so we don't end up with
 * an iframe inside a paragraph. Matches both bare URLs and URLs wrapped
 * in an <a> tag (the typical TinyMCE auto-link behaviour).
 */

const YT_ID = "([a-zA-Z0-9_-]{6,})";
const YT_HOST_PATH = "(?:youtube\\.com/watch\\?v=|youtu\\.be/|youtube\\.com/embed/)";

const P_WITH_YT_PATTERN =
  `<p\\b[^>]*>\\s*` +
  `(?:<a\\b[^>]*href="(?<hrefUrl>https?://[^"]*)"[^>]*>[^<]*</a>|` +
  `(?<bareUrl>https?://[^\\s<]+))` +
  `\\s*</p>`;

const P_WITH_YT = new RegExp(P_WITH_YT_PATTERN, "g");
const P_WITH_YT_ONESHOT = new RegExp(P_WITH_YT_PATTERN);

const VIDEO_ID_RE = new RegExp(YT_HOST_PATH + YT_ID);
const BARE_URL_RE = /^https?:\/\//;

export function makeYouTubeEmbed(videoId: string, opts?: { margins?: boolean }): string {
  // `my-6` is for embeds replacing a <p> inside flowing rich text (RichHtml).
  // Standalone placements (e.g. top of the course detail column, where the
  // parent `grid gap-6` owns the spacing) pass { margins: false } — the
  // extra margin there double-spaces and breaks top-alignment with the
  // sticky start card.
  const margin = (opts?.margins ?? true) ? "my-6 " : "";
  return (
    `<div class="${margin}aspect-video rounded-xl overflow-hidden bg-black">` +
    `<iframe ` +
    `src="https://www.youtube-nocookie.com/embed/${videoId}" ` +
    `title="YouTube video" ` +
    `loading="lazy" ` +
    `class="w-full h-full block border-0" ` +
    `allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" ` +
    `allowfullscreen></iframe></div>`
  );
}

/**
 * Find the first YouTube URL inside a rich-text HTML string. Returns the
 * video id (or null) and the html with that URL removed — so callers can
 * render the embed as a primary surface (hero player) without showing
 * the URL again in the body.
 */
export function extractFirstYouTube(html: string | null | undefined): {
  videoId: string | null;
  htmlWithoutVideo: string;
} {
  if (!html) return { videoId: null, htmlWithoutVideo: "" };

  // Bare URL — whole HTML is just the URL.
  const trimmed = html.trim();
  if (BARE_URL_RE.test(trimmed) && !trimmed.includes("<")) {
    const m = trimmed.match(VIDEO_ID_RE);
    if (m?.[1]) return { videoId: m[1], htmlWithoutVideo: "" };
  }

  // <p>-wrapped URL (bare or inside <a>).
  const match = P_WITH_YT_ONESHOT.exec(html);
  if (match) {
    const groups = match.groups as { hrefUrl?: string; bareUrl?: string };
    const url = groups.hrefUrl ?? groups.bareUrl ?? "";
    const idMatch = url.match(VIDEO_ID_RE);
    if (idMatch?.[1]) {
      const htmlWithoutVideo =
        html.slice(0, match.index) + html.slice(match.index + match[0].length);
      return { videoId: idMatch[1], htmlWithoutVideo };
    }
  }

  return { videoId: null, htmlWithoutVideo: html };
}

export function embedYouTube(html: string | null | undefined): string {
  if (!html) return "";

  // Some portal fields (e.g. courseIntro) come in as a bare URL with no
  // HTML wrapping at all — '<p>' regex below can't match that. Handle
  // it first.
  const trimmed = html.trim();
  if (BARE_URL_RE.test(trimmed) && !trimmed.includes("<")) {
    const m = trimmed.match(VIDEO_ID_RE);
    if (m?.[1]) return makeYouTubeEmbed(m[1]);
  }

  // Standard case: rich-text editor wraps the URL in <p>...</p> (bare
  // or auto-linked in <a>).
  return html.replace(P_WITH_YT, (whole, ...args) => {
    const groups = args[args.length - 1] as { hrefUrl?: string; bareUrl?: string } | undefined;
    const url = groups?.hrefUrl ?? groups?.bareUrl ?? "";
    const m = url.match(VIDEO_ID_RE);
    if (!m?.[1]) return whole;
    return makeYouTubeEmbed(m[1]);
  });
}
